import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SearchUsersRequest } from '../../../../modules/user-role/contract/requests/search-users-request.contract';
import { ViewRole } from '../../../../modules/user-role/contract/view-role.contract';
import { RolesApiService } from '../../../../modules/user-role/services/roles-api.service';
import { UsersDatasourceService } from '../../../../modules/user-role/services/users-datasource.service';
import { DropdownComponent } from '../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../osp-ui/components/form/form-field/form-field.component';
import { InputTextareaComponent } from '../../../../osp-ui/components/form/input-textarea/input-textarea.component';
import { SelectComponent } from '../../../../osp-ui/components/form/select/select.component';
import { OspTemplateDirective } from '../../../../osp-ui/directives/osp-template.directive';
import { debounce } from '../../../../shared/decorators/debounce';
import { FieldLimit } from '../../../../shared/enums/field-limit.enum';
import { EquipmentTypesGroupedSelectOption } from '../../../../shared/filters/contract/equipment-types-grouped-select-option.contract';
import { SelectOptionsConsistencyController } from '../../../../shared/utils/select-options-consistency-controller';
import { Customer } from '../../contract/customer.contract';
import { EmailRecipient, searchUserToEmailRecipient } from '../../contract/email-recipient.contract';
import { Equipment } from '../../contract/equipment.contract';
import { RoleRecipient } from '../../contract/role-recipient.contract';
import { AlarmLevel } from '../../enums/alarm-level.enum';
import { AlarmRuleControl } from '../../enums/alarm-rule-control.enum';
import {
  AlarmType,
  isAlarmTypeSupportPreventDuplicateAlarmsToggle,
  isAlarmTypeSupportSharedEquipments,
} from '../../enums/alarm-type.enum';
import {
  EquipmentAlarmRuleSelectionType,
  getEquipmentSelectionForOwnAndSharedEquipments,
  getEquipmentSelectionForOwnEquipments,
} from '../../enums/equipment-alarm-rule-selection-type.enum';
import { AlarmLevelResolverPipe } from '../../pipes/alarm-level-resolver.pipe';
import { AlarmSelectionTypeResolverPipe } from '../../pipes/alarm-selection-type-resolver.pipe';
import { AlarmTypeConfig } from '../../contract/alarm-type-config.contract';
import { EquipmentStatus } from '../../contract/equipment-status.contract';
import { LanguageService } from '../../../../core/services/language.service';
import { DropdownCustomTemplateTypes } from '../../../../osp-ui/components/form/dropdown/enums/dropdown-custom-templates.enum';
import { EquipmentStatusCategoryToDropdownSeverityPipe } from '../../../../shared/pipes/equipment-status-category-to-dropdown-severity.pipe';
import { EquipmentStatusCategoryPipe } from '../../../../shared/pipes/equipment-status-category.pipe';
import { EquipmentStatusCategory } from '../../../resources/equipment/enums/equipment-status-category.enum';

@Component({
  selector: 'osp-additional-data',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextareaComponent,
    DropdownComponent,
    TranslateModule,
    SelectComponent,
    CommonModule,
    OspTemplateDirective,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    EquipmentStatusCategoryToDropdownSeverityPipe,
  ],
  templateUrl: './additional-data.component.html',
  styleUrl: './additional-data.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalDataComponent implements OnInit, AfterViewChecked {
  private destroyRef = inject(DestroyRef);
  protected readonly AlarmType = AlarmType;

  @Input() set alarmType(type: AlarmType) {
    this._alarmType = type;
    if (this.form) {
      this.selectedAlarmTypeChanged();
    }
  }

  @Input() form!: FormGroup;
  $labelOptions = input<string[]>([], { alias: 'labelOptions' });
  $equipmentTypeOptions = input<EquipmentTypesGroupedSelectOption[]>([], { alias: 'equipmentTypeOptions' });
  $equipmentOptions = input<Equipment[]>([], { alias: 'equipmentOptions' });
  $equipmentStatusesOptions = input<EquipmentStatus[]>([], { alias: 'equipmentStatusesOptions' });
  $customerOptions = input<Customer[]>([], { alias: 'customerOptions' });
  $equipmentsLoading = input<boolean>(false, { alias: 'equipmentsLoading' });
  $equipmentStatusesLoading = input<boolean>(false, { alias: 'equipmentStatusesLoading' });
  $customersLoading = input<boolean>(false, { alias: 'customersLoading' });
  $showIsFeedbackRequiredToggle = input<boolean>(false, { alias: 'showIsFeedbackRequiredToggle' });
  $isEditMode = input<boolean>(false, { alias: 'isEditMode' });
  @Input() emailRecipients: EmailRecipient[] = [];
  @Input() roleRecipients: RoleRecipient[] = [];
  @Input() defaultEmailRecipients!: string[];
  @Input() alarmTypeConfigs!: AlarmTypeConfig[];
  @Input() isAllowedToSeeDefaultRecipients: boolean = false;
  @Input() alarmTypeConfig!: AlarmTypeConfig | undefined;
  $onEquipmentFilter = output<string>({ alias: 'onEquipmentFilter' });
  $onEquipmentStatusFilter = output<string>({ alias: 'onEquipmentStatusFilter' });
  $onCustomerFilter = output<string>({ alias: 'onCustomerFilter' });

  private _roleSuggestions$ = new BehaviorSubject<ViewRole[]>([]);
  private _userSuggestions$ = new BehaviorSubject<EmailRecipient[]>([]);
  private _alarmType!: AlarmType;

  protected readonly filterDebounceTime = environment.DELAY_SHORTEST;
  protected readonly controlNames = AlarmRuleControl;
  protected readonly selectionTypes = EquipmentAlarmRuleSelectionType;
  protected readonly fieldLimit = FieldLimit;
  protected readonly pageSizeRecipients = 100;
  protected readonly alarmLevels = Object.keys(AlarmLevel).map((alarmLevel) => ({
    label: this.alarmLevelResolver.transform(alarmLevel as AlarmLevel),
    value: alarmLevel as keyof typeof AlarmLevel,
  }));
  protected readonly $selectionTypesOptions = computed(() => {
    const supportSharedEquipments = isAlarmTypeSupportSharedEquipments(this._alarmType);

    const selectors = supportSharedEquipments
      ? getEquipmentSelectionForOwnAndSharedEquipments()
      : getEquipmentSelectionForOwnEquipments();

    return selectors.map((selectionType) => ({
      label: this.selectionTypeResolver.transform(selectionType),
      value: selectionType as keyof typeof EquipmentAlarmRuleSelectionType,
    }));
  });
  protected readonly $showPreventDuplicateAlarmsToggle = computed(() => {
    return isAlarmTypeSupportPreventDuplicateAlarmsToggle(this._alarmType);
  });
  protected readonly $formattedLabelOptions = computed(() =>
    this.$labelOptions().map((label) => ({
      value: label,
      label: label,
      key: label, // these are unique since duplicates are removed at parent
      expanded: true,
    })),
  );
  protected readonly $formattedEquipmentOptions = computed(() =>
    this.$equipmentOptions().map((equipment) => ({
      value: equipment.equipmentId,
      label: equipment.name,
      key: equipment.equipmentId,
      expanded: true,
    })),
  );
  protected readonly $formattedCustomerOptions = computed(() =>
    this.$customerOptions().map((customer) => ({
      value: customer.customerId,
      label: customer.customerName,
      key: customer.customerId,
      expanded: true,
    })),
  );
  readonly $formattedEquipmentStatusesOptions = computed(() => {
    const statuses = this.$equipmentStatusesOptions();
    const locale = this.languageService.getCurrentLocale();

    const categoryOrder = [
      EquipmentStatusCategory.OPERATIONAL,
      EquipmentStatusCategory.OPERATIONAL_WITH_RESTRICTIONS,
      EquipmentStatusCategory.NOT_OPERATIONAL,
      EquipmentStatusCategory.NOT_AVAILABLE,
    ];

    const groups: Record<
      string,
      {
        label: string;
        value: string;
        items: {
          value: string;
          label: string;
          category: string;
          originalStatus: EquipmentStatus;
        }[];
      }
    > = {};

    for (const status of statuses) {
      const category = status.category;

      if (!groups[category]) {
        groups[category] = {
          label: this.equipmentStatusCategoryPipe.transform(category),
          value: category,
          items: [],
        };
      }

      let labelName = status.statusName[locale];

      if (!labelName || labelName.trim() === '') {
        const fallbackName = Object.values(status.statusName || {}).find((val) => val && val.trim() !== '');

        labelName = (fallbackName as string) || '';
      }

      groups[category].items.push({
        value: status.equipmentStatusId,
        label: labelName,
        category: status.category,
        originalStatus: status,
      });
    }

    const sortedGroups = Object.values(groups);

    sortedGroups.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a.value as EquipmentStatusCategory);
      const indexB = categoryOrder.indexOf(b.value as EquipmentStatusCategory);

      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const rankDiff = a.originalStatus.rank - b.originalStatus.rank;

        if (rankDiff !== 0) {
          return rankDiff;
        }

        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      });
    }

    return sortedGroups;
  });
  protected readonly userSuggestions$ = this._userSuggestions$.pipe(
    map((roles) =>
      roles?.map((role) => ({
        value: role.userId,
        label: role.name,
        key: role.userId,
        expanded: true,
      })),
    ),
  );
  protected readonly roleSuggestions$ = this._roleSuggestions$.pipe(
    map((roles) =>
      roles?.map((role) => ({
        value: role.roleId,
        label: role.name,
        key: role.roleId,
        expanded: true,
      })),
    ),
  );

  private emailRecipientsOptionsController = new SelectOptionsConsistencyController<EmailRecipient>('userId');

  constructor(
    private readonly usersDatasource: UsersDatasourceService,
    private readonly rolesApiService: RolesApiService,
    private readonly alarmLevelResolver: AlarmLevelResolverPipe,
    private readonly selectionTypeResolver: AlarmSelectionTypeResolverPipe,
    private readonly languageService: LanguageService,
    private readonly equipmentStatusCategoryPipe: EquipmentStatusCategoryPipe,
  ) {
    effect(() => {
      if (this.$isEditMode()) {
        return;
      }

      const preventDuplicate = this.alarmTypeConfig?.preventDuplicateOpenAlarms ?? false;

      this.form.patchValue({
        [this.controlNames.DATA_PREVENT_DUPLICATE_OPEN_ALARMS_TOGGLE]: preventDuplicate,
      });
    });
  }

  public ngOnInit(): void {
    this.emailToggleControl?.setValue(this.emailRecipients.length > 0 || this.roleRecipients.length > 0);
    this.emailRecipientsOptionsController.setControl(this.emailRecipientsControl);
    this._userSuggestions$.next(this.emailRecipientsOptionsController.getOptions(this.emailRecipients));
    this.searchRecipientEmails();
    this.setDefaultRecipientsForNewAlarms();
    this.initListeners();
  }

  public ngAfterViewChecked(): void {
    this.selectedAlarmTypeChanged();
  }

  public get selectionTypeControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_SELECTION_TYPE);
  }

  public get labelsControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_LABELS);
  }

  public get equipmentTypesControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_EQUIPMENT_TYPES);
  }

  public get equipmentsControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_EQUIPMENTS);
  }

  public get customerControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_CUSTOMER);
  }

  public get emailToggleControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_EMAIL_TOGGLE);
  }

  public get emailRecipientsControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_EMAIL_RECIPIENTS);
  }

  public get defaultEmailRecipientsControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_DEFAULT_EMAIL_RECIPIENTS);
  }

  public get equipmentStatusesControl(): AbstractControl | null {
    return this.form.get(this.controlNames.DATA_EQUIPMENT_STATUSES);
  }

  public get alarmType(): AlarmType {
    return this._alarmType;
  }

  public get selectedType(): EquipmentAlarmRuleSelectionType {
    return this.selectionTypeControl?.value;
  }

  public get isNeedSelectionType(): boolean {
    return (
      this._alarmType !== AlarmType.TRANSPORT_TASK_CREATION && this._alarmType !== AlarmType.TRANSPORT_TASK_RETURNED
    );
  }

  @debounce(environment.DELAY_SHORTEST)
  protected searchRecipientEmails(searchTerms = ''): void {
    const params: SearchUsersRequest = { size: this.pageSizeRecipients, page: 0, terms: searchTerms };
    this.usersDatasource.requestData(params);
  }

  protected selectedTypeChanged(type: EquipmentAlarmRuleSelectionType): void {
    this.updateRequiredValidator(this.controlNames.DATA_LABELS, type === EquipmentAlarmRuleSelectionType.LABEL);
    this.updateRequiredValidator(
      this.controlNames.DATA_EQUIPMENT_TYPES,
      type === EquipmentAlarmRuleSelectionType.EQUIPMENT_TYPE,
    );
    this.updateRequiredValidator(this.controlNames.DATA_EQUIPMENTS, type === EquipmentAlarmRuleSelectionType.EQUIPMENT);
    this.updateRequiredValidator(this.controlNames.DATA_CUSTOMER, type === EquipmentAlarmRuleSelectionType.CUSTOMER);
    this.updateRequiredValidator(
      this.controlNames.DATA_EQUIPMENT_STATUSES,
      type === EquipmentAlarmRuleSelectionType.EQUIPMENT_STATUS,
    );
  }

  private initListeners(): void {
    this.usersDatasource.data$
      .pipe(
        map((users) => (users || []).map(searchUserToEmailRecipient)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users: EmailRecipient[]) => {
        this._userSuggestions$.next(this.emailRecipientsOptionsController.getOptions(users));
      });

    this.rolesApiService
      .getRolesByUserCustomer()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((roles: ViewRole[]) => {
        this._roleSuggestions$.next(roles);
      });

    this.selectionTypeControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedType: EquipmentAlarmRuleSelectionType) => this.selectedTypeChanged(selectedType));
  }

  private selectedAlarmTypeChanged(): void {
    if (this.isNeedSelectionType) {
      this.enableSelectionTypeControls();
    } else {
      this.disableSelectionTypeControls();
    }
  }

  private enableSelectionTypeControls(): void {
    [
      this.selectionTypeControl,
      this.labelsControl,
      this.equipmentTypesControl,
      this.equipmentsControl,
      this.customerControl,
      this.equipmentStatusesControl,
    ].forEach((control) => control?.enable());
  }

  private disableSelectionTypeControls(): void {
    for (const control of [
      this.selectionTypeControl,
      this.labelsControl,
      this.equipmentTypesControl,
      this.equipmentsControl,
      this.customerControl,
      this.equipmentStatusesControl,
    ]) {
      control?.setValue(null, { emitEvent: false });
      control?.disable();
    }
  }

  private updateRequiredValidator(controlName: string, isRequired: boolean): void {
    const control: UntypedFormControl = this.form.get(controlName) as UntypedFormControl;
    control.setValidators(isRequired ? Validators.required : null);
    control.updateValueAndValidity();
  }

  private setDefaultRecipientsForNewAlarms() {
    this.defaultEmailRecipientsControl?.setValue(this.defaultEmailRecipients);
  }

  resolveDefaultRecipientsPlaceholder() {
    return this.isAllowedToSeeDefaultRecipients
      ? 'modules.notification.alarmRuleAddEdit.labels.noDefaultEmailRecipientsSpecified'
      : 'modules.notification.alarmRuleAddEdit.labels.notAllowedToSee';
  }

  protected readonly dropdownCustomTemplateTypes = DropdownCustomTemplateTypes;
}
